import { dateStringToApiFormat } from "./date";

const DEFAULT_JOURNEY = {
    passengers: 1,
    luggage: 1,
    carryOn: 1,
    journeyType: "private-jets",
    flightFrom: undefined,
    flightTo: undefined,
};

/**
 * Returns a new journey object with the given id and optional overrides.
 * Shared by QuoteQueryJets, QuoteQueryHelicopters, and QuoteQueryGroupCharter.
 * @param {number} id
 * @param {Object} [overrides]
 * @returns {Object}
 */
export const createInitialJourney = (id, overrides = {}) => ({
    id,
    ...DEFAULT_JOURNEY,
    ...overrides,
});

/**
 * Returns initial journey data array for the given trip type (oneway, returns, multilegs).
 * @param {string} type
 * @returns {Object[]}
 */
export const getInitialJourneyDataForTripType = (type) => {
    if (type === "oneway") {
        return [createInitialJourney(1)];
    }
    if (type === "returns" || type === "multilegs") {
        return [createInitialJourney(1), createInitialJourney(2)];
    }
    return [createInitialJourney(1)];
};

/**
 * Computes the next journey data when the trip type changes, preserving as much
 * of the user's existing input as possible instead of clearing the legs.
 *
 * Rules:
 *  - any        -> oneway:    keep only the first leg.
 *  - any        -> returns:   keep the first leg as the outbound, and build the
 *                             inbound leg from it with From/To swapped (matches
 *                             syncReturnLegs and the "Add leg" behaviour).
 *  - oneway     -> multilegs: keep the first leg, add one empty second leg.
 *  - returns    -> multilegs: keep both existing legs as independent legs.
 *  - multilegs  -> multilegs: keep all existing legs unchanged.
 *
 * @param {string} prevType
 * @param {string} newType
 * @param {Object[]} journeys current journeyData
 * @param {number} counter current journeyCounter
 * @returns {{ journeys: Object[], counter: number }}
 */
export const transformJourneysForTripTypeChange = (prevType, newType, journeys, counter) => {
    const safe = Array.isArray(journeys) && journeys.length ? journeys : [createInitialJourney(1)];
    const first = safe[0];
    const carriedOver = {
        time: first.time,
        passengers: first.passengers ?? 1,
        luggage: first.luggage ?? 1,
        carryOn: first.carryOn ?? 1,
        journeyType: first.journeyType ?? "private-jets",
    };

    if (newType === "oneway") {
        return { journeys: [{ ...first, id: 1 }], counter: 1 };
    }

    if (newType === "returns") {
        const outbound = { ...first, id: 1 };
        const inbound = createInitialJourney(2, {
            ...carriedOver,
            flightFrom: first.flightTo,
            flightTo: first.flightFrom,
            date: "",
        });
        return { journeys: [outbound, inbound], counter: 2 };
    }

    if (newType === "multilegs") {
        // Coming from a multi-leg trip, or from a return trip: keep the legs that
        // already exist (re-indexing ids so they stay sequential).
        if ((prevType === "multilegs" || prevType === "returns") && safe.length >= 2) {
            const kept = safe.map((legItem, i) => ({ ...legItem, id: i + 1 }));
            return { journeys: kept, counter: kept.length };
        }
        // Coming from a one-way trip: keep the first leg, add one empty leg.
        const firstLeg = { ...first, id: 1 };
        const secondLeg = createInitialJourney(2, { ...carriedOver, date: "" });
        return { journeys: [firstLeg, secondLeg], counter: 2 };
    }

    return { journeys: safe, counter: counter ?? safe.length };
};

/**
 * Whether the delete-leg control should be shown for the current trip.
 * Multi-leg requires at least 3 legs before delete is available.
 * @param {number} journeyCount
 * @param {string} tripType
 * @returns {boolean}
 */
export const canDeleteJourneyLeg = (journeyCount, tripType) => {
    if (tripType === "returns") return false;
    if (tripType === "multilegs") return journeyCount >= 3;
    return journeyCount > 1;
};

/**
 * Returns the icon name for add-leg or remove-leg based on powered-by and scroll state.
 * @param {"add-leg"|"remove-leg"} baseName
 * @param {{ onPage: boolean, isScrolled: boolean, isPoweredBy: boolean }} options
 * @returns {string}
 */
export const getLegIconName = (baseName, { onPage, isScrolled, isPoweredBy }) => {
    if (!isPoweredBy) return baseName;
    let name = baseName;
    if (onPage) name += "-alt";
    if (isScrolled) name += "-scrolled";
    return name;
};

/**
 * Formats a single journey for API submit (member search).
 * @param {Object} flight
 * @param {function(string): string} formatTime
 * @returns {Object}
 */
export const formatJourneyForSubmit = (flight, formatTime) => ({
    flightFrom: flight.journeyType === "helicopters" ? flight.flightFrom : flight.flightFrom?.icao,
    flightTo: flight.journeyType === "helicopters" ? flight.flightTo : flight.flightTo?.icao,
    date: dateStringToApiFormat(flight.date),
    time: formatTime(flight.time),
    numberOfPassengers: flight.passengers,
    itemsOfLuggage: flight.luggage ?? 0,
    itemsOfCarryOn: flight.carryOn ?? 0,
    journeyType: flight.journeyType,
});

/**
 * For return trips, keeps leg 1 and leg 2 in sync when updating From/To.
 * Expects journeys to contain at least two legs; returns a new journeys array.
 * @param {Object[]} journeys
 * @param {number} index
 * @param {string} key
 * @returns {Object[]}
 */
export const syncReturnLegs = (journeys, index, key) => {
    if (!Array.isArray(journeys) || journeys.length < 2) {
        return journeys;
    }

    const next = [...journeys];
    const otherIndex = index === 0 ? 1 : index === 1 ? 0 : null;

    if (otherIndex === null) {
        return next;
    }

    if (key === "flightFrom") {
        next[otherIndex] = { ...next[otherIndex], flightTo: next[index].flightFrom };
    } else if (key === "flightTo") {
        next[otherIndex] = { ...next[otherIndex], flightFrom: next[index].flightTo };
    }

    return next;
};
